import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useState } from 'react';

// Minimal context shape for the long running task spotlight — only requires `message`
type MessageTemplateContext = { message: string };

type CustomDismissCallback = (messageInstanceId: string) => void;

const placementConfig: PlacementBrowserConfig = {
	placementId: `rovo-chat-long-running-task-spotlight`,
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		hideInIsolatedCloud: false,
	},
};

const placement = {
	createFrontend: createPlacementFrontend(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const [messages, setMessages] = useState<Array<EligibleMessage<MessageTemplateContext>>>([]);

	const apiResponse = usePlacementApi<{
		messages: Array<EligibleMessage<MessageTemplateContext>>;
	}>('/');

	useEffect(() => {
		const { error, data, isLoading } = apiResponse;

		if (isLoading) {
			return;
		}

		if (error) {
			return;
		}

		setMessages(data.messages ?? []);
	}, [apiResponse]);

	const handleDismiss: CustomDismissCallback = (messageInstanceId) => {
		setMessages(messages.filter((message) => messageInstanceId !== message.messageInstanceId));
	};

	const firstMessage = messages[0];

	if (!firstMessage) {
		return null;
	}

	return (
		<>
			<Messages.render
				key={firstMessage.messageInstanceId}
				onDismiss={() => handleDismiss(firstMessage.messageInstanceId)}
				{...firstMessage}
			/>
		</>
	);
});
